import {
  IContactRespSchema,
  IQuoteRespSchema,
  IStorage,
  QuoteDocumentConfig,
  SigningOrderEnum,
} from '@monetize/types/app';
import { UseQueryResult } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { doGetQuoteDocumentConfigDetails } from '../../../../../../api/cpqService';
import {
  MBox,
  MFlex,
  MPageLoader,
  MSpacer,
  MText,
} from '../../../../../../components/Monetize';
import { QuoteDocumentLinkV2 } from '../../../../../../components/Quotes/QuoteDocumentLinkV2';
import { useFlags } from '../../../../../../services/launchDarkly';
import {
  convertToBase64,
  showPdfInNewTab,
} from '../../../../../../utils/download';

type DocumentType = 'file' | 'link' | 'none';
interface DocumentItemState {
  savedData: {
    storage?: IStorage;
    link?: string;
  };
  activeItem: DocumentType;
  linkUrl: string;
  fileToUpload?: {
    file: File;
    fileName: string;
  };
  savedItemText: string;
}
interface EsignContentProps {
  label: string;
  fullName?: string;
  email?: string;
  isSentFirst: boolean;
  isSentBoth: boolean;
  showBorderTop?: boolean;
}

const EsignContent = ({
  label,
  fullName,
  email,
  isSentFirst,
  isSentBoth,
  showBorderTop = false,
}: EsignContentProps) => {
  let additionalText = '';
  if (!isSentBoth && isSentFirst) {
    additionalText = 'Receives the Quote first';
  } else if (isSentBoth) {
    additionalText = 'Receives the Quote at the same time';
  }

  return (
    <MFlex
      py={1}
      borderBottom={isSentFirst ? '1px solid' : 'none'}
      borderTop={showBorderTop ? '1px solid' : 'none'}
      borderColor="tGray.back"
    >
      <MBox py="2">
        <MText fontWeight="bold">{label}</MText>
        <MText fontSize="xs" color="tGray.darkPurple">
          {additionalText}
        </MText>
      </MBox>
      <MSpacer />
      <MBox py="2">
        <MText fontWeight="400" color="tGray.darkPurple" textAlign="right">
          {fullName}
        </MText>
        <MText
          fontSize="xs"
          fontWeight="400"
          color="tGray.darkPurple"
          textAlign="right"
        >
          {email}
        </MText>
      </MBox>
    </MFlex>
  );
};

interface ContentContentProps {
  quote: IQuoteRespSchema;
  internalContact?: IContactRespSchema;
  externalContact?: IContactRespSchema;
  sowDocument: UseQueryResult<IStorage, unknown>;
  msaDocument: UseQueryResult<IStorage, unknown>;
  attachedDocuments: UseQueryResult<QuoteDocumentConfig[], unknown>;
}

export const SendQuoteThroughEsignContent = ({
  quote,
  internalContact,
  externalContact,
  sowDocument,
  msaDocument,
  attachedDocuments,
}: ContentContentProps) => {
  const { enableQuoteDocumentManagement } = useFlags();
  const [msaData, setMsaData] = useState<DocumentItemState>({
    savedData: enableQuoteDocumentManagement
      ? {
          link: quote.documentUrl || undefined,
        }
      : {
          storage: msaDocument.data,
          link: quote.documentUrl || undefined,
        },
    activeItem: 'none',
    linkUrl: '',
    savedItemText: '',
  });

  const [sowData, setSowData] = useState<DocumentItemState>({
    savedData: {
      storage: sowDocument.data,
    },
    activeItem: 'none',
    linkUrl: '',
    savedItemText: '',
  });

  // Set initial state id msaDocument changes
  useEffect(() => {
    if (msaDocument.data || quote.documentUrl) {
      setMsaData((priorData) => {
        let activeItem: DocumentType = 'none';
        if (msaDocument.data) {
          activeItem = 'file';
        } else if (quote.documentUrl) {
          activeItem = 'link';
        }
        return {
          ...priorData,
          savedData: {
            link: quote.documentUrl || undefined,
            storage: msaDocument.data,
          },
          activeItem,
          savedItemText: msaDocument.data?.filename || quote.documentUrl || '',
        };
      });
    }
  }, [msaDocument.data, quote.documentUrl]);

  useEffect(() => {
    if (sowDocument.data) {
      setSowData((priorData) => {
        let activeItem: DocumentType = 'none';
        if (sowDocument.data) {
          activeItem = 'file';
        }
        return {
          ...priorData,
          savedData: {
            link: undefined,
            storage: sowDocument.data,
          },
          activeItem,
          savedItemText: sowDocument.data?.filename || '',
        };
      });
    }
  }, [sowDocument.data?.filename, sowDocument.data]);

  const externalESign = (
    <EsignContent
      key={externalContact?.id || 'external'}
      label={`${quote?.accountName || 'External'} Signature`}
      fullName={externalContact?.fullName}
      email={externalContact?.email}
      isSentFirst={quote.signingOrder === SigningOrderEnum.EXTERNAL_FIRST}
      isSentBoth={quote.signingOrder === SigningOrderEnum.INSTANTANEOUS}
    />
  );
  const internalESign = (
    <EsignContent
      key={internalContact?.id || 'internal'}
      label={`${quote.fromCompany.name || 'Internal'} Signature`}
      fullName={internalContact?.fullName}
      email={internalContact?.email}
      isSentFirst={quote.signingOrder === SigningOrderEnum.INTERNAL_FIRST}
      isSentBoth={quote.signingOrder === SigningOrderEnum.INSTANTANEOUS}
    />
  );

  const eSignContent = [externalESign];
  if (quote.signingOrder !== SigningOrderEnum.EXTERNAL_ONLY) {
    if (quote.signingOrder === SigningOrderEnum.INTERNAL_FIRST) {
      eSignContent.unshift(internalESign);
    } else {
      eSignContent.push(internalESign);
    }
  }

  const isLoadingInitialData = msaDocument.isLoading || sowDocument.isLoading;

  async function handleGoToAttachedDoc(docId: string) {
    const doc = await doGetQuoteDocumentConfigDetails(quote.id, docId, true);
    if (doc.storageObject?.contentBase64Encoded) {
      showPdfInNewTab(
        doc.storageObject.contentBase64Encoded,
        doc.storageObject.filename,
      );
    }
  }

  async function handleGoToItem(type: 'msa' | 'sow') {
    const getter = type === 'msa' ? msaData : sowData;
    if (getter.activeItem === 'file') {
      let base64Data = '';
      let fileName = '';
      if (getter.fileToUpload) {
        base64Data = await convertToBase64(getter.fileToUpload.file);
        // eslint-disable-next-line prefer-destructuring
        fileName = getter.fileToUpload.fileName;
      } else if (getter.savedData.storage?.contentBase64Encoded) {
        base64Data = getter.savedData.storage.contentBase64Encoded;
        fileName = getter.savedData.storage.filename;
      }
      showPdfInNewTab(base64Data, fileName);
    } else {
      const link = getter.linkUrl || getter.savedData.link;
      if (link) {
        window.open(link, '_blank');
      }
    }
  }

  if (isLoadingInitialData) {
    return <MPageLoader maxH={100} />;
  }

  return (
    <MBox>
      {eSignContent}
      {quote.contacts.additionalSigners.length > 0 && (
        <>
          {quote.contacts.additionalSigners.map((signer, index) => (
            <EsignContent
              key={index}
              label="Additional Signer"
              fullName={signer.fullName}
              email={signer.email}
              isSentFirst={false}
              isSentBoth={false}
              showBorderTop={true}
            />
          ))}
        </>
      )}
      {quote.contacts.additionalESignRecipients.length > 0 && (
        <MFlex py={1} borderTop="1px solid" borderColor="tGray.back">
          <MBox py="2">
            <MText fontWeight="bold">Additional Recipients</MText>
          </MBox>
          <MSpacer />
          <MBox py="2">
            {/* TODO: likely a "show more" pattern if there are a lot - e.g. + 10 more */}
            {quote.contacts.additionalESignRecipients.map((recipient) => (
              <MText
                key={recipient.id}
                fontWeight="400"
                color="tGray.darkPurple"
                textAlign="right"
              >
                {recipient.email}
              </MText>
            ))}
          </MBox>
        </MFlex>
      )}
      {!enableQuoteDocumentManagement && (
        <MFlex
          py={4}
          justifyContent="space-between"
          borderTop="1px solid"
          borderColor="tGray.back"
        >
          <MText minW="fit-content" fontWeight={600}>
            MSA
          </MText>

          {(msaDocument.data || quote.documentUrl) && (
            <QuoteDocumentLinkV2
              onClick={() => handleGoToItem('msa')}
              fileName={
                quote.documentUrl || msaDocument?.data?.referenceFilename || ''
              }
              showLinkIcon={!!quote.documentUrl}
            />
          )}
          {!quote.documentUrl &&
            !msaDocument.isFetching &&
            !msaDocument.data && (
              <MText fontStyle="italic">No document added</MText>
            )}
        </MFlex>
      )}

      {enableQuoteDocumentManagement && quote.documentUrl && (
        <MFlex
          py={4}
          justifyContent="space-between"
          borderTop="1px solid"
          borderColor="tGray.back"
        >
          <MText minW="fit-content" fontWeight={600}>
            MSA Url
          </MText>

          <QuoteDocumentLinkV2
            onClick={() => handleGoToItem('msa')}
            fileName={quote.documentUrl || ''}
            showLinkIcon={!!quote.documentUrl}
          />

          {!quote.documentUrl &&
            !msaDocument.isFetching &&
            !msaDocument.data && (
              <MText fontStyle="italic">No document added</MText>
            )}
        </MFlex>
      )}

      {!enableQuoteDocumentManagement && (
        <MFlex
          py={4}
          justifyContent="space-between"
          borderTop="1px solid"
          borderColor="tGray.back"
        >
          <MText minW="fit-content" fontWeight={600}>
            SOW
          </MText>
          {sowDocument.data && (
            <QuoteDocumentLinkV2
              onClick={() => handleGoToItem('sow')}
              fileName={sowDocument.data.referenceFilename || ''}
            />
          )}
          {!sowDocument.isFetching && !sowDocument.data && (
            <MText fontStyle="italic">No document added</MText>
          )}
        </MFlex>
      )}

      {enableQuoteDocumentManagement &&
        attachedDocuments.data?.map((doc) => (
          <MFlex
            key={doc.id}
            py={4}
            justifyContent="space-between"
            borderTop="1px solid"
            borderColor="tGray.back"
          >
            <MText minW="fit-content" fontWeight={600}>
              {doc.quoteDocumentType.label}
            </MText>
            <QuoteDocumentLinkV2
              onClick={() => handleGoToAttachedDoc(doc.id)}
              fileName={
                doc.storageObject.referenceFilename ||
                doc.storageObject.filename ||
                ''
              }
            />
          </MFlex>
        ))}
    </MBox>
  );
};
